import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { provideApolloClient } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { PRODUCT_MUTATION_UPDATE, PRODUCTS_QUERY_LIST, PRODUCTS_QUERY } from '../graphql';
import { useProduct } from '../composables/useProduct';
import type { Filter } from '~/components/ui/data/DataTable.vue';
import type { ProductsQueryListQuery, ProductsQueryQuery } from '~/types/graphql/graphql';
import { useApollo } from '~/composables/config/useApollo';

export const productStore = defineStore('productStore', () => {
  const search = ref('');
  const loading = ref(false);
  const products = ref<ProductsQueryListQuery['products']>([]);
  const currentProduct = ref<ProductsQueryQuery['products_by_pk'] | null>(null);
  const error = ref<string | null>(null);
  const filterBy = ref<Filter>({
    name: 'last-production',
    type: 'desc',
  });
  const { t } = useI18n();
  const { showToast } = toastStore();
  const { selectedSite } = globalStore();
  const { getLastProductionToUnix, getAverageEPI } = useProduct();

  const getFilter = computed(() => filterBy.value);

  const getProducts = computed(() => {
    return products.value.filter((product) => {
      return (
        product.label?.toLocaleLowerCase().includes(search.value.toLocaleLowerCase()) ||
        product.client_product_id.toString().includes(search.value)
      );
    });
  });

  const getProduct = (id: number) => {
    return products.value.find((product) => product.id === id);
  };

  const getProductFiltered = computed(() => {
    const data = getProducts.value;

    return data.sort((a, b) => {
      if (filterBy.value?.name === 'last-production') {
        if (filterBy.value.type === 'asc') return getLastProductionToUnix(a) - getLastProductionToUnix(b);
        if (!a.fabrication_order_products?.length) return 1;
        if (!b.fabrication_order_products?.length) return -1;
        return getLastProductionToUnix(b) - getLastProductionToUnix(a);
      }
      if (filterBy.value?.name === 'client-product-id') {
        if (filterBy.value.type === 'asc') return parseInt(a.client_product_id) - parseInt(b.client_product_id);
        return parseInt(b.client_product_id) - parseInt(a.client_product_id);
      }
      if (filterBy.value?.name === 'epi') {
        if (filterBy.value.type === 'asc') return getAverageEPI(a).average - getAverageEPI(b).average;
        return getAverageEPI(b).average - getAverageEPI(a).average;
      }
      return 0;
    });
  });

  async function fetchProducts() {
    loading.value = true;
    const { apolloClient } = useApollo();
    provideApolloClient(apolloClient);

    try {
      const result = await apolloClient.query<ProductsQueryListQuery>({
        query: PRODUCTS_QUERY_LIST,
        variables: {
          site_id: selectedSite,
        },
      });
      products.value = result.data.products;
    } catch (e: any) {
      error.value = e.message;
    }

    loading.value = false;
  }

  async function fetchProduct(id: number) {
    loading.value = true;
    const { apolloClient } = useApollo();
    provideApolloClient(apolloClient);

    try {
      const result = await apolloClient.query<ProductsQueryQuery>({
        query: PRODUCTS_QUERY,
        variables: {
          id: id,
        },
      });
      currentProduct.value = result.data.products_by_pk;
    } catch (e: any) {
      error.value = e.message;
    }

    loading.value = false;
  }

  async function updateProduct(id: number, product: { label?: string; epi_standard?: number }) {
    const { apolloClient } = useApollo();
    provideApolloClient(apolloClient);

    try {
      await apolloClient.mutate({
        mutation: PRODUCT_MUTATION_UPDATE,
        variables: {
          id: id,
          object: product,
        },
      });
    } catch (e: any) {
      showToast({
        type: 'error',
        message: t('global.error'),
        title: t('product.update.error'),
      });
      error.value = e.message;
    }
    await fetchProducts();
  }

  function updateFilter(filter: Filter) {
    filterBy.value = filter;
  }

  return {
    search,
    loading,
    products,
    currentProduct,
    error,
    filterBy,
    getFilter,
    getProducts,
    getProduct,
    getProductFiltered,
    fetchProducts,
    fetchProduct,
    updateProduct,
    updateFilter,
  };
});
